import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { useMediaQuery } from '@mui/material';
import { Skeleton } from '@mui/material';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import { Typography } from '@components/Typography';
import { Switch } from '@components/Switch';
import { Select } from '@components/Select';
import { MultiSelect } from '@components/MultiSelect';
import { googleIntegrationService, facebookIntegrationService } from '@services';
import { googleActions, facebookActions, alertActions, accountActions } from '@actions';
import { LocationIntegrationName } from '@constants/integrations';
import { STORE_TYPE } from '@constants/profiles';
import { handleApiError } from '@helpers/api';
import { useTranslation, useYupValidationResolver } from '@hooks';
import { Modal, Content } from './styles';
import { ignoredFieldOptions } from './ignoredFields';

const ManageProfileModal = ({ integrationType, open, onCloseModal, selectedCompany, setDisconnectModalOpen }) => {
    const isMobile = useMediaQuery('@media (max-width: 600px)');
    const { t } = useTranslation();
    const isFacebookModal = integrationType === LocationIntegrationName.Facebook;
    const isGoogleModal = integrationType === LocationIntegrationName.Google;

    const data = isGoogleModal
        ? useSelector(state => state.google.listing)
        : useSelector(state => state.facebook.listing);
    const loadingGoogle = useSelector(state => state.google.loadingListing);
    const loadingFacebook = useSelector(state => state.facebook.loadingListing);
    const loading = isFacebookModal ? loadingFacebook : loadingGoogle;

    const dispatch = useDispatch();

    const ProfileSchema = yup.object().shape({
        storeLocationType: yup.string().nullable(),
        selectedStoreLocation: yup.string().when('storeLocationType', {
            is: STORE_TYPE.CONNECT_STORE,
            then: schema => schema.required('validation.required'),
            otherwise: schema => schema.nullable(),
        }),
    });

    const resolver = useYupValidationResolver(ProfileSchema);

    const {
        setValue,
        control,
        handleSubmit,
        watch,
        reset,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm({
        mode: 'all',
        resolver,
        defaultValues: {
            storeLocationType: STORE_TYPE.MAIN_PAGE,
            selectedStoreLocation: '',
            syncEnabled: true,
            ignoredSyncFields: [],
        },
    });

    useEffect(() => {
        if (selectedCompany) {
            isGoogleModal && dispatch(googleActions.getListing(selectedCompany.companyId));
            isFacebookModal && dispatch(facebookActions.getListing(selectedCompany.companyId));
        }
    }, [selectedCompany]);

    useEffect(() => {
        if (Object.keys(data).length > 0) {
            reset({
                storeLocationType: isFacebookModal ? data?.type?.toString() || '1' : STORE_TYPE.MAIN_PAGE,
                selectedStoreLocation: isFacebookModal ? data?.selectedStoreLocation?.id || '' : '',
                syncEnabled: data.syncEnabled,
                ignoredSyncFields: data.ignoredSyncFields ?? [],
            });
        }
    }, [data, reset]);

    const loadedStoreLocationType = isFacebookModal ? data?.type?.toString() || '1' : STORE_TYPE.MAIN_PAGE;
    const loadedStoreLocation = isFacebookModal ? data?.selectedStoreLocation?.id || '' : '';
    const pageChanged =
        watch('storeLocationType') !== loadedStoreLocationType ||
        (watch('storeLocationType') === STORE_TYPE.CONNECT_STORE &&
            watch('selectedStoreLocation') !== loadedStoreLocation);

    const onSubmit = async values => {
        const ignoredSyncFields = pageChanged || !values.syncEnabled ? undefined : values.ignoredSyncFields;
        try {
            if (isGoogleModal) {
                await googleIntegrationService.updateLocationListing(selectedCompany.companyId, {
                    syncEnabled: values.syncEnabled,
                    ignoredSyncFields,
                });
            }
            if (isFacebookModal) {
                await facebookIntegrationService.updateLocationListing(selectedCompany.companyId, {
                    type: +values.storeLocationType,
                    selectedStoreLocation:
                        data?.storeLocations?.find(location => location.id === values.selectedStoreLocation) || null,
                    syncEnabled: values.syncEnabled,
                    ignoredSyncFields,
                    locationId: selectedCompany?.companyId,
                });
                await dispatch(accountActions.get());
            }
            dispatch(alertActions.success(t('alertMessages.editSuccess')));
            onClose();
        } catch (error) {
            handleApiError(error, 'alertMessages.editFail');
        }
    };

    const onClose = () => {
        reset();
        onCloseModal();
        dispatch(facebookActions.setListing({}));
        dispatch(googleActions.setListing({}));
    };

    const storeType = watch('storeLocationType');
    const syncEnabled = watch('syncEnabled');
    const fieldOptions = useMemo(
        () => ignoredFieldOptions({ comparedFields: data?.comparedSyncFields, syncDiff: data?.syncDiff, t }),
        [data?.comparedSyncFields, data?.syncDiff, t],
    );
    const offeredFields = fieldOptions.map(option => option.value);
    const unofferedIgnores = (data?.ignoredSyncFields ?? []).filter(field => !offeredFields.includes(field));

    const RADIO_OPTIONS = [
        {
            label: t('Listings.connectMainPage'),
            value: STORE_TYPE.MAIN_PAGE,
            disabled: false,
        },
        {
            label: t('Listings.connectStoreLocation'),
            value: STORE_TYPE.CONNECT_STORE,
            disabled: !data?.storeLocations?.length,
        },
    ];

    return (
        <Modal
            fullScreen
            isOpen={open}
            handleClose={onClose}
            isDirty={isDirty}
            title={isGoogleModal ? t('Listings.modalTitleGoogle') : t('Listings.modalTitleFacebook')}
            onPrimaryAction={handleSubmit(onSubmit)}
            primaryActionText={t('buttons.saveChanges')}
            primaryActionDisabled={isSubmitting || !isDirty || !isValid}
            onAdditionalAction={() => setDisconnectModalOpen(true)}
            additionalTextButton={t('buttons.disconnect')}
            additionalActionDisabled={isSubmitting}
        >
            {data && !loading && (
                <Content>
                    {isFacebookModal && data.locationName && (
                        <div>
                            <Typography variant="caption" color="textSecondary">
                                {t('SettingsCompanies.locationName')}
                            </Typography>
                            <Typography variant="body2">{data.locationName}</Typography>
                        </div>
                    )}
                    {isFacebookModal && (
                        <Controller
                            control={control}
                            name="storeLocationType"
                            render={({ field, ref }) =>
                                RADIO_OPTIONS.map((item, index) => (
                                    <RadioGroup key={index} {...field} ref={ref}>
                                        <FormControlLabel
                                            value={`${item.value}`}
                                            control={
                                                <Radio
                                                    disabled={item.disabled}
                                                    sx={{
                                                        color: 'var(--text-secondary)',
                                                        padding: '6px',
                                                        '&.Mui-checked': { color: 'var(--primary-main)' },
                                                    }}
                                                />
                                            }
                                            label={
                                                <Typography variant="body2" sx={{ marginLeft: '10px' }}>
                                                    {item.label}
                                                </Typography>
                                            }
                                            disabled={item.disabled}
                                            sx={{ marginLeft: '-3px' }}
                                        />
                                    </RadioGroup>
                                ))
                            }
                        />
                    )}
                    {isFacebookModal && storeType === STORE_TYPE.CONNECT_STORE && (
                        <Controller
                            control={control}
                            name="selectedStoreLocation"
                            render={({ field, fieldState: { error }, ref }) => (
                                <Select
                                    {...field}
                                    ref={ref}
                                    onChange={({ target: { value } }) => {
                                        setValue('selectedStoreLocation', value, {
                                            shouldDirty: true,
                                            shouldValidate: true,
                                        });
                                    }}
                                    options={
                                        data?.storeLocations?.map(location => ({
                                            value: location.id,
                                            label: location.name,
                                        })) || []
                                    }
                                    required
                                    label={t('Listings.storeLocation')}
                                    fullWidth
                                    dropdownStyles={{ maxHeight: '300px' }}
                                    error={!!error}
                                    helperText={error ? t(error.message) : null}
                                    sx={{ width: isMobile ? '100%' : '50%', marginTop: '8px' }}
                                />
                            )}
                        />
                    )}
                    <Controller
                        control={control}
                        name="syncEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                sx={{ marginBottom: '16px' }}
                                checked={field.value}
                                handleChange={event => {
                                    setValue('syncEnabled', event.target.checked, {
                                        shouldDirty: true,
                                    });
                                }}
                                label={t('Listings.enableProfileSync')}
                            />
                        )}
                    />
                    {syncEnabled && !pageChanged && (
                        <Controller
                            control={control}
                            name="ignoredSyncFields"
                            render={({ field }) => (
                                <div>
                                    <MultiSelect
                                        id="ignored-sync-fields"
                                        countable
                                        label={t('Listings.ignoredFields')}
                                        options={fieldOptions}
                                        value={field.value.filter(value => offeredFields.includes(value))}
                                        onChange={value =>
                                            setValue('ignoredSyncFields', [...value, ...unofferedIgnores], {
                                                shouldDirty: true,
                                            })
                                        }
                                    />
                                    <Typography variant="caption" color="textSecondary" sx={{ marginTop: '4px' }}>
                                        {t('Listings.ignoredFieldsHint')}
                                    </Typography>
                                </div>
                            )}
                        />
                    )}
                </Content>
            )}
            {loading && (
                <div style={{ display: 'grid', rowGap: '32px' }}>
                    <Skeleton variant="rounded" width="50%" height={40} />
                    <Skeleton variant="rounded" width="50%" height={40} />
                </div>
            )}
        </Modal>
    );
};

export default ManageProfileModal;
